import { RobotApi } from 'api/RobotApi'
import { StationApi } from 'api/StationApi'
import Loader from 'components/Loader/Loader'
import { useUrlQueryState } from 'hooks/useUrlQueryState'
import { useUser } from 'hooks/user'
import AssetsView from 'pages/Assets/AssetsView'
import { parseAssetsTab } from 'pages/Assets/assetsTab'
import { sortRobotsForAssetsList } from 'pages/Assets/assetsRobotsSort'
import {
  sortStationsForAssetsList,
  applyLiveStationSortFields,
  mergeStationsFromList,
} from 'pages/Assets/assetsStationsSort'
import { useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from 'react'
import { PROGRESS_TYPES, TIMEOUT_FETCHING_STATUS } from 'utils/constants'
import { ILot, IRobotInfo, IStation } from 'utils/interfaces'
import { lotTypeMap } from 'utils/lotInterfaces'
import { applyStateAction, enumParamUpper, flagParam, pageParam, stringParam } from 'utils/urlQueryState'
import { sortWithFavoritesFirst } from 'utils/favoritesSort'
import { VENDOR, filterRobotsByEnabledVendors } from 'utils/vendorDisplayName'

const DEFAULT_PAGE_SIZE = 20
type AssetsVendorFilter = typeof VENDOR.AUTONOMICS | typeof VENDOR.GAUSIUM | typeof VENDOR.ASBIS | 'ALL'

const Assets = () => {
  const user = useUser()
  const [url, setUrl] = useUrlQueryState({
    tab: {
      parse: parseAssetsTab,
      serialize: (tab: ReturnType<typeof parseAssetsTab>) => (tab === 'robots' ? undefined : tab),
    },
    vendor: enumParamUpper(['ALL', 'AUTONOMICS', 'GAUSIUM', 'ASBIS'] as const, 'ALL'),
    q: stringParam(),
    sq: stringParam(),
    page: pageParam(),
    spage: pageParam(),
    rfav: flagParam(),
    sfav: flagParam(),
  })
  const [loadingProgress, setLoadingProgress] = useState(PROGRESS_TYPES.IDLE)
  const [robots, setRobots] = useState<IRobotInfo[]>([])
  const [stations, setStations] = useState<IStation[]>([])
  const [lots, setLots] = useState<ILot[]>([])
  const [robotsPageSize, setRobotsPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [robotsPageInput, setRobotsPageInput] = useState('1')
  const [stationsPageSize, setStationsPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [stationsPageInput, setStationsPageInput] = useState('1')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(url.q)
  const [debouncedStationSearchQuery, setDebouncedStationSearchQuery] = useState(url.sq)
  const skipPageResetRef = useRef(true)
  const cancelFetchRef = useRef<(() => void) | null>(null)

  const displayTab = url.tab
  const vendorFilter = url.vendor as AssetsVendorFilter
  const searchQuery = url.q
  const stationSearchQuery = url.sq
  const robotsCurrentPage = url.page
  const stationsCurrentPage = url.spage

  const setActiveTab = (tab: ReturnType<typeof parseAssetsTab>) => setUrl({ tab })
  const setVendorFilter = (vendor: AssetsVendorFilter) => setUrl({ vendor, page: 1 })
  const setSearchQuery = (q: string) => setUrl({ q })
  const setStationSearchQuery = (sq: string) => setUrl({ sq })
  const setRobotsCurrentPage = (page: SetStateAction<number>) =>
    setUrl(s => ({ page: applyStateAction(s.page, page) }))
  const setStationsCurrentPage = (spage: SetStateAction<number>) =>
    setUrl(s => ({ spage: applyStateAction(s.spage, spage) }))
  const setRobotsFavoritesOnly = (rfav: boolean) => setUrl({ rfav, page: 1 })
  const setStationsFavoritesOnly = (sfav: boolean) => setUrl({ sfav, spage: 1 })

  const fetchStationsAndLots = useCallback(async () => {
    const [stationsData, lotsData] = await Promise.all([
      StationApi.fetchStations(),
      StationApi.fetchLots(),
    ])
    setStations(prev => mergeStationsFromList(prev, stationsData))
    setLots(lotsData)
  }, [])

  const fetchRobots = useCallback(async () => {
    const { results } = await RobotApi.fetchRobots(
      1,
      1000,
      debouncedSearchQuery,
      undefined,
      undefined,
      cancelFn => {
        cancelFetchRef.current = cancelFn
      },
      true
    )
    const filteredByAvailability = filterRobotsByEnabledVendors(results)
    const filteredResults =
      vendorFilter === 'ALL'
        ? filteredByAvailability
        : filteredByAvailability.filter(r => r.vendor === vendorFilter)
    setRobots(filteredResults)
  }, [debouncedSearchQuery, vendorFilter])

  const fetchData = useCallback(
    async (isInitial = false) => {
      if (cancelFetchRef.current) {
        cancelFetchRef.current()
        cancelFetchRef.current = null
      }

      try {
        if (isInitial) {
          setLoadingProgress(PROGRESS_TYPES.WORK)
        }
        // Lots and the station list are only used by the Stations tab.
        if (displayTab === 'stations') {
          await fetchStationsAndLots()
        }
        if (displayTab === 'robots') {
          await fetchRobots()
        }
        setLoadingProgress(PROGRESS_TYPES.SUCCESS)
      } catch (error: unknown) {
        if (error instanceof Error && error.name === 'CancelError') {
          return
        }
        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          if (isInitial) {
            setLoadingProgress(PROGRESS_TYPES.IDLE)
          }
          return
        }
        if (isInitial) {
          setLoadingProgress(PROGRESS_TYPES.ERROR)
        }
        console.warn('[Assets] fetchData error:', error)
      } finally {
        cancelFetchRef.current = null
      }
    },
    [displayTab, fetchRobots, fetchStationsAndLots]
  )

  const refreshStationsAndLots = useCallback(async () => {
    await fetchStationsAndLots()
  }, [fetchStationsAndLots])

  const handleLiveStationFields = useCallback((fieldsById: Record<number, Partial<IStation>>) => {
    setStations(prev => applyLiveStationSortFields(prev, fieldsById))
  }, [])

  const handleRobotsUpdated = useCallback(() => {
    void fetchRobots()
  }, [fetchRobots])

  const handleStationsUpdated = useCallback(() => {
    void fetchData(true)
  }, [fetchData])

  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout> | undefined
    let active = true

    const poll = async () => {
      await fetchData()
      if (active) {
        timerId = setTimeout(poll, TIMEOUT_FETCHING_STATUS)
      }
    }

    void fetchData(true).then(() => {
      if (active) {
        timerId = setTimeout(poll, TIMEOUT_FETCHING_STATUS)
      }
    })

    return () => {
      active = false
      if (timerId) {
        clearTimeout(timerId)
      }
      if (cancelFetchRef.current) {
        cancelFetchRef.current()
        cancelFetchRef.current = null
      }
    }
  }, [fetchData])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedStationSearchQuery(stationSearchQuery), 300)
    return () => clearTimeout(timer)
  }, [stationSearchQuery])

  useEffect(() => {
    if (skipPageResetRef.current) return
    if (robotsCurrentPage !== 1) setRobotsCurrentPage(1)
  }, [debouncedSearchQuery, vendorFilter, url.rfav])

  useEffect(() => {
    if (skipPageResetRef.current) return
    if (stationsCurrentPage !== 1) setStationsCurrentPage(1)
  }, [debouncedStationSearchQuery, url.sfav])

  useEffect(() => {
    skipPageResetRef.current = false
  }, [])

  const favoriteRobotIds = useMemo(
    () => new Set(user?.state.favoriteRobotIds ?? []),
    [user?.state.favoriteRobotIds]
  )
  const favoriteStationIds = useMemo(
    () => new Set(user?.state.favoriteStationIds ?? []),
    [user?.state.favoriteStationIds]
  )

  /** Pre-index lot search text per station — lots are already loaded; no extra API. */
  const lotSearchTextByStationId = useMemo(() => {
    const map = new Map<number, string>()
    for (const lot of lots) {
      const typeLabel = (lotTypeMap[lot.type] || '').toLowerCase()
      const model = (lot.lot_model || '').toLowerCase()
      const lotName = (lot.name || '').toLowerCase()
      const chunk = [typeLabel, model, lotName].filter(Boolean).join(' ')
      if (!chunk) {
        continue
      }
      const prev = map.get(lot.robot_station)
      map.set(lot.robot_station, prev ? `${prev} ${chunk}` : chunk)
    }
    return map
  }, [lots])

  const sortedRobots = useMemo(() => {
    const base = url.rfav ? robots.filter(robot => favoriteRobotIds.has(robot.id)) : robots
    return sortWithFavoritesFirst(sortRobotsForAssetsList(base), favoriteRobotIds)
  }, [robots, url.rfav, favoriteRobotIds])
  const robotsTotalCount = sortedRobots.length

  const filteredStations = useMemo(() => {
    const q = debouncedStationSearchQuery.trim().toLowerCase()
    const searched = !q
      ? stations
      : stations.filter(station => {
          const name = (station.name || '').toLowerCase()
          const stationId = (station.station_id || '').toLowerCase()
          const description = (station.description || '').toLowerCase()
          const lotsText = lotSearchTextByStationId.get(station.id) || ''
          return name.includes(q) || stationId.includes(q) || description.includes(q) || lotsText.includes(q)
        })
    const base = url.sfav ? searched.filter(station => favoriteStationIds.has(station.id)) : searched
    return sortWithFavoritesFirst(sortStationsForAssetsList(base), favoriteStationIds)
  }, [stations, debouncedStationSearchQuery, lotSearchTextByStationId, url.sfav, favoriteStationIds])

  const stationsTotalCount = filteredStations.length

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(robotsTotalCount / robotsPageSize))
    if (robotsCurrentPage > maxPage) {
      setRobotsCurrentPage(maxPage)
    }
  }, [robotsCurrentPage, robotsPageSize, robotsTotalCount])

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(stationsTotalCount / stationsPageSize))
    if (stationsCurrentPage > maxPage) {
      setStationsCurrentPage(maxPage)
    }
  }, [stationsCurrentPage, stationsPageSize, stationsTotalCount])

  useEffect(() => {
    setRobotsPageInput(robotsCurrentPage.toString())
  }, [robotsCurrentPage])

  useEffect(() => {
    setStationsPageInput(stationsCurrentPage.toString())
  }, [stationsCurrentPage])

  const handleRobotsPageJump = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const val = parseInt(robotsPageInput)
      const maxPage = Math.ceil(robotsTotalCount / robotsPageSize) || 1
      if (!isNaN(val) && val >= 1 && val <= maxPage) {
        setRobotsCurrentPage(val)
      } else {
        setRobotsPageInput(robotsCurrentPage.toString())
      }
    }
  }

  const handleStationsPageJump = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const val = parseInt(stationsPageInput)
      const maxPage = Math.ceil(stationsTotalCount / stationsPageSize) || 1
      if (!isNaN(val) && val >= 1 && val <= maxPage) {
        setStationsCurrentPage(val)
      } else {
        setStationsPageInput(stationsCurrentPage.toString())
      }
    }
  }

  const currentRobots = useMemo(
    () =>
      sortedRobots.slice(
        (robotsCurrentPage - 1) * robotsPageSize,
        robotsCurrentPage * robotsPageSize
      ),
    [sortedRobots, robotsCurrentPage, robotsPageSize]
  )
  const currentStations = useMemo(
    () =>
      filteredStations.slice(
        (stationsCurrentPage - 1) * stationsPageSize,
        stationsCurrentPage * stationsPageSize
      ),
    [filteredStations, stationsCurrentPage, stationsPageSize]
  )

  if (loadingProgress === PROGRESS_TYPES.WORK && robots.length === 0 && stations.length === 0) {
    return <Loader />
  }

  return (
    <AssetsView
      variant="page"
      activeTab={displayTab}
      onActiveTabChange={setActiveTab}
      robots={currentRobots}
      robotsForMap={sortedRobots}
      stations={currentStations}
      stationsForMap={filteredStations}
      lots={lots}
      onRobotsUpdated={handleRobotsUpdated}
      onStationsChanged={refreshStationsAndLots}
      onStationsUpdated={handleStationsUpdated}
      onLiveStationFields={handleLiveStationFields}
      loadingProgress={loadingProgress}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      robotsFavoritesOnly={url.rfav}
      setRobotsFavoritesOnly={setRobotsFavoritesOnly}
      stationSearchQuery={stationSearchQuery}
      setStationSearchQuery={setStationSearchQuery}
      stationsFavoritesOnly={url.sfav}
      setStationsFavoritesOnly={setStationsFavoritesOnly}
      vendorFilter={vendorFilter}
      setVendorFilter={setVendorFilter}
      robotsCurrentPage={robotsCurrentPage}
      setRobotsCurrentPage={setRobotsCurrentPage}
      robotsTotalCount={robotsTotalCount}
      robotsPageSize={robotsPageSize}
      onRobotsPageSizeChange={setRobotsPageSize}
      robotsPageInput={robotsPageInput}
      setRobotsPageInput={setRobotsPageInput}
      handleRobotsPageJump={handleRobotsPageJump}
      stationsCurrentPage={stationsCurrentPage}
      setStationsCurrentPage={setStationsCurrentPage}
      stationsTotalCount={stationsTotalCount}
      stationsPageSize={stationsPageSize}
      onStationsPageSizeChange={setStationsPageSize}
      stationsPageInput={stationsPageInput}
      setStationsPageInput={setStationsPageInput}
      handleStationsPageJump={handleStationsPageJump}
    />
  )
}

export default Assets
